import React, { useEffect, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  GOOGLE_CLIENT_ID, 
  initializeGoogleAuth, 
  renderGoogleButton 
} from '../config/googleAuth';
import googleAnalytics from '../services/googleAnalytics';
import storageUtils from '../utils/storage';

const GoogleAuthButton = ({ 
  mode = 'signin', // 'signin' or 'signup'
  onSuccess = () => {},
  onError = () => {},
  buttonText = 'Continue with Google',
  style = {}
}) => {
  const googleButtonRef = useRef(null);
  const customButtonRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { login, register, setUser, clearAllUserData } = useAuth();
  const navigate = useNavigate();

  // Initialize Google Auth on component mount
  useEffect(() => {
    const initGoogle = async () => {
      try {
        const initialized = await initializeGoogleAuth();
        setIsInitialized(initialized);
        
        if (initialized && googleButtonRef.current) {
          // Set up the credential response handler
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
          });
          
          // Render the Google button (hidden)
          renderGoogleButton(googleButtonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: mode === 'signin' ? 'signin_with' : 'signup_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: '100%'
          });
        }
      } catch (error) {
        console.error('Failed to initialize Google Auth:', error);
        setIsInitialized(false);
      }
    };

    initGoogle();
  }, [mode]);

  // Handle Google credential response
  const handleCredentialResponse = async (response) => {
    setIsLoading(true);
    
    try {
      // Clear any previous user's data before Google authentication
      if (clearAllUserData) {
        clearAllUserData();
      }
      // Decode the JWT token to get user info
      const decoded = jwtDecode(response.credential);
      console.log('Decoded Google token:', decoded);
      
      const googleUserData = {
        email: decoded.email,
        name: decoded.name,
        firstName: decoded.given_name,
        lastName: decoded.family_name,
        picture: decoded.picture,
        googleId: decoded.sub,
        emailVerified: decoded.email_verified
      };

      // Send Google token to backend for verification
      console.log('Sending to backend:', mode, googleUserData);
      const backendResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: response.credential,
          userData: googleUserData,
          mode: mode // 'signin' or 'signup'
        })
      });

      const result = await backendResponse.json();
      console.log('Backend response:', result);
      console.log('Backend status:', backendResponse.status);
      console.log('result.success:', result.success);
      console.log('result.user:', result.user);
      console.log('result.token:', result.token);
      
      if (result.success || backendResponse.status === 200) {
        // Backend already handled authentication/registration
        console.log('Google auth success, user data:', result.user);
        
        // If no user object returned, decode it from the JWT token
        let userData = result.user;
        if (!userData && result.token) {
          try {
            const decodedToken = jwtDecode(result.token);
            userData = {
              id: decodedToken.userId,
              email: decodedToken.email,
              isGoogleAuth: decodedToken.isGoogleAuth
            };
            console.log('Extracted user from token:', userData);
          } catch (e) {
            console.error('Failed to decode token:', e);
          }
        }
        
        // Normalize user data to ensure consistent _id field
        if (userData.id && !userData._id) {
          userData._id = userData.id;
        }
        
        // Set the user and token using storage utility for mobile compatibility
        // Also store in the expected localStorage format for consistency
        storageUtils.setAuthTokens(result.token, userData);
        localStorage.setItem('userToken', result.token);
        localStorage.setItem('userId', userData._id);
        localStorage.setItem('userData', JSON.stringify(userData));
        
        setUser(userData);
        
        // Track Google authentication success
        if (mode === 'signup') {
          googleAnalytics.trackAuth.signup('google');
          googleAnalytics.trackConversion.signupComplete();
        } else {
          googleAnalytics.trackAuth.login('google');
        }
        
        onSuccess(userData);
        
        // Small delay to ensure state updates before navigation
        setTimeout(() => {
          navigate('/dashboard');
        }, 100);
      } else if (backendResponse.status === 400 && result.message && result.message.includes('already exists')) {
        // Account exists - try to sign in instead  
        console.log('Google signup failed: Account exists, attempting signin automatically...');
        
        // Retry with signin mode
        const signinResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: response.credential,
            userData: googleUserData,
            mode: 'signin'
          })
        });
        
        const signinResult = await signinResponse.json();
        if (signinResult.success) {
          // Normalize user data to ensure consistent _id field
          if (signinResult.user.id && !signinResult.user._id) {
            signinResult.user._id = signinResult.user.id;
          }
          
          storageUtils.setAuthTokens(signinResult.token, signinResult.user);
          localStorage.setItem('userToken', signinResult.token);
          localStorage.setItem('userId', signinResult.user._id);
          localStorage.setItem('userData', JSON.stringify(signinResult.user));
          
          setUser(signinResult.user);
          onSuccess(signinResult.user);
          setTimeout(() => {
            navigate('/dashboard');
          }, 100);
        } else {
          throw new Error('Account already exists but automatic sign-in failed. Please try signing in manually.');
        }
      } else {
        // Handle other Google auth errors with user-friendly messages
        let errorMessage = result.message || 'Google authentication failed';
        
        if (result.message && result.message.toLowerCase().includes('not found')) {
          errorMessage = 'No account found. Please create an account first or try with a different Google account.';
        } else if (result.message && result.message.toLowerCase().includes('invalid')) {
          errorMessage = 'Google authentication failed. Please try again or contact support.';
        }
        
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error('Google Auth Error:', error);
      onError(error);
      
      // Fallback: show user-friendly error based on error type
      let userMessage = `Google ${mode === 'signin' ? 'Sign In' : 'Sign Up'} failed. Please try again or use email/password.`;
      
      if (error.message && error.message.includes('already exists')) {
        userMessage = 'Account already exists. Please sign in instead or use the regular login form.';
      } else if (error.message && error.message.includes('not found')) {
        userMessage = 'No account found with this Google account. Please create an account first.';
      }
      
      alert(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle custom button click
  const handleCustomButtonClick = () => {
    if (!isInitialized) {
      console.error('Google Auth not initialized');
      return;
    }

    // Check if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Mobile-specific handling
      try {
        // Disable auto-select for mobile
        window.google.accounts.id.disableAutoSelect();
        
        // Create a temporary container for the Google button
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'fixed';
        tempContainer.style.top = '50%';
        tempContainer.style.left = '50%';
        tempContainer.style.transform = 'translate(-50%, -50%)';
        tempContainer.style.zIndex = '10000';
        tempContainer.style.background = 'white';
        tempContainer.style.padding = '20px';
        tempContainer.style.borderRadius = '8px';
        tempContainer.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)';
        
        document.body.appendChild(tempContainer);
        
        // Render the actual Google button
        window.google.accounts.id.renderButton(tempContainer, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: mode === 'signin' ? 'signin_with' : 'signup_with',
          width: '300',
          click_listener: () => {
            // Remove the container after click
            setTimeout(() => {
              document.body.removeChild(tempContainer);
            }, 100);
          }
        });
        
        // Auto-click the button after a brief delay
        setTimeout(() => {
          const googleButton = tempContainer.querySelector('div[role="button"]');
          if (googleButton) {
            googleButton.click();
          } else {
            // Fallback: remove container if button not found
            document.body.removeChild(tempContainer);
          }
        }, 100);
        
      } catch (error) {
        console.error('Mobile Google Auth error:', error);
        // Fallback to desktop prompt
        window.google.accounts.id.prompt();
      }
    } else {
      // Desktop prompt flow
      window.google.accounts.id.prompt();
    }
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      {/* Hidden Google button for functionality */}
      <div 
        ref={googleButtonRef} 
        style={{ 
          position: 'absolute', 
          top: '-9999px', 
          left: '-9999px',
          visibility: 'hidden' 
        }}
      />
      
      {/* Custom styled button */}
      <button
        ref={customButtonRef}
        type="button"
        onClick={handleCustomButtonClick}
        disabled={isLoading || !isInitialized}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          padding: '14px 16px',
          backgroundColor: '#000000',
          border: 'none',
          borderRadius: '0',
          fontSize: '14px',
          fontWeight: '600',
          color: '#FFFFFF',
          cursor: isLoading || !isInitialized ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          textTransform: 'none',
          letterSpacing: '0.02em',
          opacity: isLoading || !isInitialized ? 0.6 : 1,
          boxShadow: '4px 4px 0px #000000',
          ...(style.button || {})
        }}
      >
        {isLoading ? (
          <>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid rgba(255,255,255,0.3)',
              borderTop: '2px solid #FFFFFF',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            Processing...
          </>
        ) : (
          <>
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.582C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            {buttonText}
          </>
        )}
      </button>

      {/* Loading spinner animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GoogleAuthButton;